/**
 * TemplateLabelsModal — название шаблона и подписи его переменных.
 *
 * Правим ровно то, что принадлежит нам: текст и статус — зона Meta,
 * сюда они приходят только на чтение. Подписи задаются по порядку
 * плейсхолдеров и столько же, сколько их в теле — это проверяет бэк,
 * поэтому поля рисуем от `variables`, а не от уже сохранённых подписей:
 * шаблон могли поменять в Meta, и подписей станет меньше, чем нужно.
 *
 * Пустое название отправляем как `null` — бэк понимает это как
 * «вернуть имя из Meta». Отправь мы пустую строку, названием шаблона
 * стала бы пустая строка, и в кабинете клиники он стал бы безымянным.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {setTemplateLabels, type Template} from '@services/templates';
import {keys} from '@queries/keys';
import {Field, TextInput} from '@components/FormKit';
import {extractErrorMessage} from '@utils/httpError';
import {ModalShell} from '@pages/Clients/ClientCard/ModalShell';

type Props = {
  /** Что правим; null — модалка закрыта. */
  template: Template | null;
  onClose: () => void;
};

const FAILED = 'Не удалось сохранить шаблон';

export const TemplateLabelsModal = ({template, onClose}: Props) => {
  const queryClient = useQueryClient();
  const open = template !== null;

  const [title, setTitle] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [prevId, setPrevId] = useState<string | null>(null);

  /* Форму наполняем при смене шаблона, а не в эффекте: иначе на один
     кадр показались бы поля от предыдущего. */
  if (template !== null && template.id !== prevId) {
    setPrevId(template.id);
    setTitle(template.title);
    setLabels(template.variables.map((_, i) => template.variableLabels[i] ?? ''));
  }
  if (template === null && prevId !== null) setPrevId(null);

  const mutation = useMutation({
    mutationFn: setTemplateLabels,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.templates.all});
      onClose();
    },
  });

  const handleSubmit = () => {
    if (template === null) return;
    mutation.mutate({id: template.id, title, variableLabels: labels});
  };

  const setLabelAt = (i: number, value: string) =>
    setLabels((prev) => prev.map((x, j) => (j === i ? value : x)));

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Название и подписи"
      subtitle={
        template !== null ? (
          <>
            Шаблон <span className="font-mono text-foreground">{template.metaName}</span>. Название
            и подписи видит клиника в своём кабинете; текст шаблона меняется только в Meta.
          </>
        ) : undefined
      }
      submitLabel="Сохранить"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      error={mutation.isError ? extractErrorMessage(mutation.error, FAILED) : null}
    >
      <Field label="Название для кабинета клиники">
        <TextInput
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 120))}
          placeholder={template?.metaName ?? 'Напоминание о приёме'}
        />
      </Field>

      {template !== null && template.variables.length > 0 && (
        <div className="mt-3.5 flex flex-col gap-2.5">
          <div className="text-[12.5px] font-semibold text-skr-ink2">Подписи переменных</div>
          {template.variables.map((v, i) => (
            /* Ключ по индексу намеренно: тело может использовать один
               плейсхолдер дважды, и `key={v}` дал бы дубли. */
            <div key={i} className="flex items-center gap-2.5">
              <span className="w-[42px] shrink-0 font-mono text-[12px] text-skr-faint">{v}</span>
              <TextInput
                className="flex-1"
                value={labels[i] ?? ''}
                onChange={(e) => setLabelAt(i, e.target.value)}
                placeholder="Например: Имя"
              />
            </div>
          ))}
        </div>
      )}

      {template !== null && template.variables.length === 0 && (
        <div className="mt-3.5 rounded-[10px] bg-skr-head px-[13px] py-2 text-[12.5px] text-skr-ink2">
          У шаблона нет переменных — подставлять нечего.
        </div>
      )}
    </ModalShell>
  );
};
